import cx from "classnames";
import { Location } from "history";
import { debounce } from "lodash";
import React from "react";
import { connect } from "react-redux";

import {
    adSlotRefresh,
    adSlotRegister,
    adSlotUnregister,
} from "@/mk2/containers/AdSlot/AdSlot.actions";
import { composeTransform, dbg, getMaxHeightWithAd } from "@/mk2/containers/AdSlot/AdSlot.helpers";
import { AdSlotsState } from "@/mk2/containers/AdSlot/AdSlot.reducers";
import { getAdSlotsState } from "@/mk2/containers/AdSlot/AdSlot.selectors";
import { ZoneTargeting } from "@/mk2/containers/AdSlot/interfaces";
import { PREFIX_AD_SLOT_INTERSTITIAL } from "@/mk2/containers/AdSlotInterstitial/AdSlotInterstitial";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { AppState, getRequestDeviceMobile, getRequestUser } from "@/mk2/reducers";
import { UserEntity } from "@/mk2/schemas";
import { hasAds } from "@/mk/bazaar/common/userUtils";
import { AD_UNITS, Collapse, isBrandingDesktopAdUnit } from "@/mk/ox/hbAdUnits";

import styles from "./AdSlot.mscss";

// Per-prefix counter ensures stable IDs regardless of mount order between SSR and client
const dynamicAdCountByPrefix: Record<string, number> = {};
let registeredSlots: {
    [slotId: string]: { slotId: string; zone: string; targeting: ZoneTargeting };
} = {};

const MARGIN_CLASSNAMES = {
    top: {
        3: "AdSlot--marginTop3",
        10: "AdSlot--marginTop10",
        20: "AdSlot--marginTop20",
        30: "AdSlot--marginTop30",
    },
    bottom: {
        3: "AdSlot--marginBottom3",
        10: "AdSlot--marginBottom10",
        20: "AdSlot--marginBottom20",
        30: "AdSlot--marginBottom30",
    },
};

export interface OwnProps {
    className?: string;
    style?: any;
    zone: string;
    targeting?: ZoneTargeting;
    prefix?: string;
    forceShowAds?: boolean;
    location: Location;
    children?: React.ReactNode;

    slotRef?: React.RefObject<HTMLDivElement>;
    withoutDiv?: boolean;

    // Delay ad registration until the slot scrolls near the viewport.
    // Uses IntersectionObserver with 600px rootMargin.
    lazyLoad?: boolean;

    // TODO: CeMi vyriesit krajsie, tak ze sa refactorne ako sa generuje slotId.
    // Ak budeme mat slotId v OwnProps, mozeme sa ist do reduxu opytat , ci sa nejaka
    // reklama zobrazila a podla toho vygenerovat DOM
    marginTopWithAd?: 3 | 10 | 20 | 30;
    marginBottomWithAd?: 3 | 10 | 20 | 30;

    allowDownscaling?: boolean;
    parentWidth?: number;
}

interface StateProps {
    requestUser: UserEntity;
    isMobile: boolean;
    adSlots: AdSlotsState;
}

interface DispatchProps {
    onRegister(slotId: string, zone: string, targeting: ZoneTargeting);
    onUnregister(slotId: string, zone: string);
    onRefresh(slotId: string);
}

type Props = OwnProps & StateProps & DispatchProps;

declare const window;

interface State {
    slotId: string;
    wrapperDivWidthWin: number;
    wrapperDivHeightWin: number;
}

export const compareForShowOnThisDevice = (isMobile, zone) => {
    return isMobile
        ? !zone.endsWith("(Desktop)") && !zone.endsWith("- Desktop") // ignoruj Desktop-only plochy na mobile
        : !zone.endsWith("(Mobile)") && !zone.endsWith("- Mobile"); // ignoruj Mobile-only plochy na desktope
};

class AdSlotComponent extends React.PureComponent<Props, State> {
    public static defaultProps: Pick<OwnProps, "prefix"> = {
        prefix: "mk-slot",
    };

    private wrapperDivRef = React.createRef<HTMLDivElement>();
    private creativeRefElement: HTMLDivElement | null = null;
    private lazyObserver: IntersectionObserver | null = null;
    private paralaxFrame: number | null = null;

    private handleWrapperDivSizeWithGetBoundClientRect = debounce(() => {
        const el = this.wrapperDivRef.current;
        if (el) {
            const rect = el.getBoundingClientRect();
            const width = rect.width;
            const height = rect.height;

            const { wrapperDivWidthWin, wrapperDivHeightWin } = this.state;
            if (wrapperDivWidthWin !== width || wrapperDivHeightWin !== height) {
                this.setState({
                    wrapperDivWidthWin: width,
                    wrapperDivHeightWin: height,
                });
            }
        }
    }, 100);

    private handleCreativeRef = (el: HTMLDivElement | null) => {
        this.creativeRefElement = el;
        // Forward ref to parent if provided
        const slotRef = this.props.slotRef as any;
        if (slotRef) {
            if (typeof slotRef === "function") {
                slotRef(el);
            } else if (slotRef.current !== undefined) {
                slotRef.current = el;
            }
        }
    };

    private static PARALLAX_STICK_TOP = 44; // px from top of viewport where creative sticks

    private handleParallax = () => {
        if (this.paralaxFrame !== null) {
            cancelAnimationFrame(this.paralaxFrame);
        }
        this.paralaxFrame = requestAnimationFrame(() => {
            const wrapper = this.wrapperDivRef.current;
            const creative = this.creativeRefElement;

            if (!wrapper || !creative) {
                this.paralaxFrame = null;
                return;
            }

            // Interscroller vkladá div s clip-path cez insertAdjacentHTML —
            // ak ho nájdeme, preskočíme parallax (transform láme position:fixed)
            if (creative.querySelector('[style*="clip-path"]')) {
                creative.style.transform = "";
                this.paralaxFrame = null;
                return;
            }

            const wrapperRect = wrapper.getBoundingClientRect();
            const creativeHeight = creative.scrollHeight;
            const wrapperHeight = wrapper.offsetHeight;
            const stickTop = AdSlotComponent.PARALLAX_STICK_TOP;
            const maxOffset = Math.max(0, wrapperHeight - creativeHeight);

            // Koľko pixelov by kreativa musela byť posunutá aby ostala na stickTop
            const offset = Math.max(0, stickTop - wrapperRect.top);
            // Limituj na maximum aby kreativa nedošla mimo placeholder
            const clampedOffset = Math.min(offset, maxOffset);

            // Single DOM write point — len transform, žiadne position/top
            creative.style.transform = composeTransform({ translateY: clampedOffset });

            this.paralaxFrame = null;
        });
    };

    public constructor(props: Props) {
        super(props);

        const slotId = this.generateSlotId();
        this.state = {
            slotId,
            wrapperDivWidthWin: 0,
            wrapperDivHeightWin: 0,
        };

        if (
            process.env.SERVER &&
            !props.lazyLoad &&
            (props.forceShowAds || hasAds(props.requestUser))
        ) {
            registeredSlots[slotId] = {
                slotId,
                zone: props.zone,
                targeting: props.targeting,
            };
        }
    }

    public componentDidMount() {
        if (this.props.lazyLoad && this.wrapperDivRef.current) {
            const rect = this.wrapperDivRef.current.getBoundingClientRect();
            dbg("[ads:dbg] lazyLoad:mount", {
                zone: this.props.zone,
                rect: {
                    top: Math.round(rect.top),
                    bottom: Math.round(rect.bottom),
                    height: Math.round(rect.height),
                },
                distanceFromViewportBottom: Math.round(rect.top - window.innerHeight),
            });
            this.lazyObserver = new IntersectionObserver(
                (entries) => {
                    dbg("[ads:dbg] lazyLoad:IO", {
                        zone: this.props.zone,
                        isIntersecting: entries[0]?.isIntersecting,
                        boundingClientRect: entries[0]?.boundingClientRect
                            ? {
                                  top: Math.round(entries[0].boundingClientRect.top),
                                  height: Math.round(entries[0].boundingClientRect.height),
                              }
                            : null,
                        rootBounds: entries[0]?.rootBounds
                            ? {
                                  top: Math.round(entries[0].rootBounds.top),
                                  bottom: Math.round(entries[0].rootBounds.bottom),
                              }
                            : null,
                        ratio: entries[0]?.intersectionRatio,
                    });
                    if (entries[0]?.isIntersecting) {
                        this.lazyObserver?.disconnect();
                        this.lazyObserver = null;
                        this.register();
                    }
                },
                { rootMargin: "1500px" },
            );
            this.lazyObserver.observe(this.wrapperDivRef.current);
        } else {
            this.register();
        }
        window.addEventListener("resize", this.handleWrapperDivSizeWithGetBoundClientRect);
        this.handleWrapperDivSizeWithGetBoundClientRect(); // hneď meraj aj pri mount

        // Enable parallax pre autorefresh inline sloty
        const { zone, isMobile } = this.props;
        const adUnit = AD_UNITS[zone];
        const isAutorefreshInline = adUnit?.autorefresh && !zone.includes("Anchor");
        if (isAutorefreshInline && isMobile) {
            window.addEventListener("scroll", this.handleParallax, { passive: true });
        }
    }

    public UNSAFE_componentWillReceiveProps(nextProps: Props) {
        // Zone has changed - unregister
        // NOTE: Should we also test targeting (deep equal)? - currently no
        if (this.props.zone !== nextProps.zone) {
            this.unregister();

            const newSlotId = this.generateSlotId();
            if (this.state.slotId !== newSlotId) {
                this.setState({
                    slotId: newSlotId,
                });
            }
        }
    }

    public componentDidUpdate(prevProps: Props) {
        // If interstitial, don't re-register / refresh
        if (this.props.prefix === PREFIX_AD_SLOT_INTERSTITIAL) {
            return;
        }

        // Zone has changed - register
        // NOTE: Should we also test targeting (deep equal)? - currently no
        if (prevProps.zone !== this.props.zone) {
            this.register();
        }

        if (
            !prevProps.location ||
            !this.props.location ||
            prevProps.location.key !== this.props.location.key
        ) {
            this.refresh();
        }

        this.handleWrapperDivSizeWithGetBoundClientRect();
    }

    public componentWillUnmount() {
        this.lazyObserver?.disconnect();
        this.lazyObserver = null;
        if (this.paralaxFrame !== null) {
            cancelAnimationFrame(this.paralaxFrame);
        }
        window.removeEventListener("resize", this.handleWrapperDivSizeWithGetBoundClientRect);
        window.removeEventListener("scroll", this.handleParallax);
        this.handleWrapperDivSizeWithGetBoundClientRect.cancel(); // zruší debounce pri unmount
        this.unregister();
    }

    public render() {
        const {
            className,
            forceShowAds,
            isMobile,
            requestUser,
            children,
            marginTopWithAd,
            marginBottomWithAd,
            zone,
            withoutDiv,
            adSlots,
            parentWidth,
            allowDownscaling,
        } = this.props;

        const slotId = this.state.slotId;

        if (!this.showOnThisDevice() || (!forceShowAds && !hasAds(requestUser))) {
            return null;
        }

        const adSlot = adSlots[slotId];
        const adSlotLastSize = adSlot?.lastSize;
        const { adSlotWidth, adSlotHeight } = sanitizeSize(adSlotLastSize);
        const hasSize =
            Boolean(adSlotLastSize) &&
            adSlotWidth &&
            adSlotWidth > 1 &&
            adSlotHeight &&
            adSlotHeight > 1;

        const adUnit = AD_UNITS[zone];

        // let downscale a branding with buggy size
        const buggyBranding =
            adUnit &&
            isBrandingDesktopAdUnit(adUnit) &&
            adSlotWidth &&
            adSlotHeight &&
            1020 < adSlotWidth &&
            adSlotWidth <= 1339 &&
            250 < adSlotHeight &&
            adSlotHeight <= 345;

        let stylesForScaledWrapper = {};
        let stylesForScaledAd = {};
        let downscaleValue: number | undefined;
        const wrapperWidth = parentWidth || this.state.wrapperDivWidthWin;
        if (allowDownscaling || buggyBranding) {
            const { scaledWidth, scaledHeight, scale } = getDownscaledSize(
                wrapperWidth,
                adSlotWidth,
                adSlotHeight + (marginTopWithAd || 0) + (marginBottomWithAd || 0),
            );
            if (adSlotWidth && adSlotHeight && scale && scale !== 1) {
                downscaleValue = scale;
                stylesForScaledWrapper = {
                    overflow: "hidden",
                    height: `${scaledHeight}px`,
                    width: `${scaledWidth}px`,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                };
                stylesForScaledAd = {
                    width: `${adSlotWidth}px`,
                    height: `${adSlotHeight}px`,
                    transform: composeTransform({ scale }),
                    transformOrigin: "center",
                };
            }
        }

        const adSlotIsEmpty = adSlot?.isEmpty === true;
        const isAutorefreshInline = adUnit.autorefresh && !zone.includes("Anchor");
        // Slot responded with an empty fill → should collapse (no placeholder, no wrapper min-height)
        const shouldCollapseAfterFetch =
            adSlotIsEmpty && adUnit.collapse === Collapse.AFTER_FETCH && !isAutorefreshInline;

        const maxHeightWithAd = getMaxHeightWithAd(adUnit, isMobile);
        let placeholderHeight: number;
        if (isAutorefreshInline && maxHeightWithAd) {
            // Autorefresh inline slots: fixed height always to prevent layout shift
            placeholderHeight =
                (marginTopWithAd || 0) + (maxHeightWithAd || 0) + (marginBottomWithAd || 0);
        } else if (
            !hasSize &&
            (adUnit.collapse === Collapse.NEVER ||
                (adUnit.collapse === Collapse.AFTER_FETCH && !adSlotIsEmpty))
        ) {
            placeholderHeight =
                (marginTopWithAd || 0) + (maxHeightWithAd || 0) + (marginBottomWithAd || 0);
        }

        // Override CSS class min-height (e.g. AdSlotBranding__desktop has min-height: 250px)
        // so the wrapper collapses to 0 when the slot is empty.
        const outerWrapperStyle = shouldCollapseAfterFetch
            ? { minHeight: 0, ...stylesForScaledWrapper }
            : stylesForScaledWrapper;

        // Autorefresh inline na mobile: minHeight ide na wrapper, creative má auto height
        const useParallax = isAutorefreshInline && isMobile;

        if (process.env.NODE_ENV === "development" && useParallax && downscaleValue) {
            console.warn(
                `[AdSlot] parallax + downscaling active simultaneously on "${zone}". ` +
                    "These features use separate transforms — verify the visual result.",
            );
        }

        if (withoutDiv) {
            return children || null;
        }

        return (
            <div
                style={{
                    ...outerWrapperStyle,
                    ...(useParallax
                        ? { minHeight: placeholderHeight, position: "relative", overflow: "hidden" }
                        : {}),
                }}
                ref={this.wrapperDivRef}
                className={className}
            >
                <div style={stylesForScaledAd} className={styles.AdSlot__wrapper}>
                    <div
                        suppressHydrationWarning
                        id={slotId}
                        className={cx(
                            styles.AdSlot,
                            isAutorefreshInline && styles["AdSlot--autorefresh"],
                            // apply margins and min height on inner div (if ad filled)
                            marginTopWithAd && styles[MARGIN_CLASSNAMES.top[marginTopWithAd]],
                            marginBottomWithAd &&
                                styles[MARGIN_CLASSNAMES.bottom[marginBottomWithAd]],
                        )}
                        ref={this.handleCreativeRef}
                        style={{
                            // parallax: creative má auto height; inak placeholder gap
                            ...(useParallax ? {} : { minHeight: placeholderHeight }),
                            ...this.props.style,
                        }}
                    >
                        {children}
                    </div>
                </div>
            </div>
        );
    }

    private refresh() {
        const { onRefresh, forceShowAds, requestUser } = this.props;

        if (this.showOnThisDevice() && (forceShowAds || hasAds(requestUser))) {
            onRefresh(this.state.slotId);
        }
    }

    private showOnThisDevice() {
        const { isMobile, zone } = this.props;
        return compareForShowOnThisDevice(isMobile, zone);
    }

    private generateSlotId = () => {
        const prefix = this.props.prefix;
        const count = dynamicAdCountByPrefix[prefix] || 0;
        dynamicAdCountByPrefix[prefix] = count + 1;
        const slug = this.props.zone
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "");
        return `${prefix}-${count}--${slug}`;
    };

    private register = () => {
        const { zone, targeting, onRegister, forceShowAds, requestUser } = this.props;
        if (this.showOnThisDevice() && (forceShowAds || hasAds(requestUser))) {
            dbg("[ads:dbg] lazyLoad:register", { zone, slotId: this.state.slotId });
            dbg(`[ads:dbg] AdSlot register:onRegister`);
            onRegister(this.state.slotId, zone, targeting);
        }
    };

    private unregister = () => {
        const { zone, onUnregister, forceShowAds, requestUser } = this.props;
        if (this.showOnThisDevice() && (forceShowAds || hasAds(requestUser))) {
            onUnregister(this.state.slotId, zone);
        }
    };
}

function mapStateToProps(state: AppState): StateProps {
    return {
        requestUser: getRequestUser(state),
        isMobile: getRequestDeviceMobile(state),
        adSlots: getAdSlotsState(state),
    };
}

const mapDispatchToProps: MapDispatchToPropsObject<DispatchProps> = {
    onRegister: adSlotRegister,
    onUnregister: adSlotUnregister,
    onRefresh: adSlotRefresh,
};

export const AdSlot = connect(mapStateToProps, mapDispatchToProps)(AdSlotComponent);

export function resetAdCount() {
    for (const key of Object.keys(dynamicAdCountByPrefix)) {
        delete dynamicAdCountByPrefix[key];
    }
}

export function clearRegisteredSlots() {
    registeredSlots = {};
}

export function getRegisteredSlots() {
    return registeredSlots;
}

export function sanitizeSize(size: number[] | string) {
    let adSlotWidth = null;
    let adSlotHeight = null;
    if (size) {
        if (Array.isArray(size)) {
            adSlotWidth = size[0];
            adSlotHeight = size[1];
        } else if (typeof size === "string") {
            const sizeParts = size.split("x");
            if (sizeParts.length === 2) {
                adSlotWidth = parseInt(sizeParts[0], 10);
                adSlotHeight = parseInt(sizeParts[1], 10);
            }
        }
    }
    return {
        adSlotWidth,
        adSlotHeight,
    };
}

export function getDownscaledSize(
    parentWidth: number,
    adSlotWidth: number,
    adSlotHeight: number,
): { scale: number; scaledWidth: number; scaledHeight: number } {
    let scale = 1;
    if (parentWidth && adSlotWidth) {
        const rawScale = parentWidth / adSlotWidth;
        scale = isFinite(rawScale) && rawScale > 0 && rawScale < 1 ? rawScale : 1;
    }

    return {
        scale,
        scaledWidth: Math.round(adSlotWidth * scale),
        scaledHeight: Math.round(adSlotHeight * scale),
    };
}
